/* eslint-disable jsx-a11y/anchor-has-content */
import React, { forwardRef } from 'react';
import { Col, Row } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import './index.less';

import {
  ConfluenceSourceParametersLoadingMode,
  ConfluenceSourceParametersSettings,
  IngestionSourceParametersOutput,
  IngestionSourceParametersType,
} from '../../../../../../gamma-api';
import IbTypography from '../../../../../components/common/IbTypography';
import IbInput from '../../../../../components/common/IbInput';
import IbTextArea from '../../../../../components/common/IbTextArea';
import Switcher from '../../../../../components/common/Switcher';
import { FormAPI } from '../../../../../types';
import IbFormFieldLabel from '../../../../../components/common/IbFormFieldLabel';

import { MAIN_CLASS_NAME } from './const';
import { useValidationHandle } from './useValidationHandle';

const getPageUrlsIsValid = (loadingMode?: ConfluenceSourceParametersLoadingMode, pageUrls?: string[]) =>
  loadingMode !== ConfluenceSourceParametersLoadingMode.Pages || pageUrls?.join('\n').trim().length;

const getSpaceKeyIsValid = (loadingMode?: ConfluenceSourceParametersLoadingMode, spaceKey?: string | null) =>
  loadingMode !== ConfluenceSourceParametersLoadingMode.Space || spaceKey?.trim().length;

export interface ISourceSettingsConfluenceProps {
  parameters: IngestionSourceParametersOutput;
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
}

const SourceSettingsConfluence = forwardRef<FormAPI, ISourceSettingsConfluenceProps>(
  ({ parameters, onChange }, ref) => {
    const { t } = useTranslation();

    const settings = parameters.settings as ConfluenceSourceParametersSettings;

    const [errors, setErrors] = useValidationHandle(
      ref,
      (isTouched: boolean) => {
        return {
          name: { isTouched: isTouched, isError: !parameters.name },
          base_url: { isTouched: isTouched, isError: !settings.base_url },
          page_urls: { isTouched: isTouched, isError: !getPageUrlsIsValid(settings.loading_mode, settings.page_urls) },
          space_key: { isTouched: isTouched, isError: !getSpaceKeyIsValid(settings.loading_mode, settings.space_key) },
        };
      },
      [parameters.name, settings.base_url, settings.loading_mode, settings.page_urls, settings.space_key]
    );

    const validatePageUrls = (loadingMode?: ConfluenceSourceParametersLoadingMode, pageUrls?: string[]) => {
      setErrors({
        ...errors,
        page_urls: {
          isTouched: true,
          isError: !getPageUrlsIsValid(loadingMode, pageUrls),
        },
      });
    };

    const validateSpaceKey = (loadingMode?: ConfluenceSourceParametersLoadingMode, spaceKey?: string | null) => {
      setErrors({
        ...errors,
        space_key: {
          isTouched: true,
          isError: !getSpaceKeyIsValid(loadingMode, spaceKey),
        },
      });
    };

    const onNameChange = (value: string) => {
      onChange?.({
        ...parameters,
        name: value,
      });

      setErrors({ ...errors, name: { isError: !value, isTouched: true } });
    };

    const onNameBlur = () => setErrors({ ...errors, name: { isError: errors.name.isError, isTouched: true } });

    const onBaseUrlChange = (value: string) => {
      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          source_type: IngestionSourceParametersType.Confluence,
          base_url: value,
        },
      });

      setErrors({ ...errors, base_url: { isError: !value, isTouched: true } });
    };

    const onBaseUrlBlur = () =>
      setErrors({ ...errors, base_url: { isError: errors.base_url.isError, isTouched: true } });

    const onLoadingModeChange = (value: string | boolean) => {
      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          source_type: IngestionSourceParametersType.Confluence,
          loading_mode: value as ConfluenceSourceParametersLoadingMode,
        },
      });
    };

    const onPageUrlsChange = (value: string) => {
      const urls = value.split('\n').map((id) => id.trim());

      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          source_type: IngestionSourceParametersType.Confluence,
          page_urls: value.split('\n').map((id) => id.trim()),
        },
      });

      validatePageUrls(settings.loading_mode, urls);
    };

    const onPageUrlsBlur = () => validatePageUrls(settings.loading_mode, settings.page_urls);

    const onSpaceKeyChange = (value: string) => {
      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          source_type: IngestionSourceParametersType.Confluence,
          space_key: value,
        },
      });

      validateSpaceKey(settings.loading_mode, value);
    };

    const onSpaceKeyBlur = () => validateSpaceKey(settings.loading_mode, settings.space_key);

    const onApiKeyChange = (value: string) => {
      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          source_type: IngestionSourceParametersType.Confluence,
          auth_token: value,
        },
      });
    };

    const loadingModeOptions = [
      {
        value: ConfluenceSourceParametersLoadingMode.Pages,
        label: t('Source settings: Confluence: LoadingMode: Pages'),
      },
      {
        value: ConfluenceSourceParametersLoadingMode.Space,
        label: t('Source settings: Confluence: LoadingMode: Space'),
      },
    ];

    const renderSpaceKey = () => {
      if (settings.loading_mode !== ConfluenceSourceParametersLoadingMode.Space) {
        return null;
      }

      return (
        <Col span={24}>
          <IbFormFieldLabel
            hint={t('Source settings: Confluence: Space key: Hint')}
            label={t('Source settings: Confluence: Space key: Title')}
          />
          <IbInput
            status={errors.space_key.isTouched && errors.space_key.isError ? 'error' : 'default'}
            value={settings.space_key}
            onBlur={onSpaceKeyBlur}
            onChange={onSpaceKeyChange}
          />
          {errors.space_key.isTouched && errors.space_key.isError && (
            <IbTypography.Paragraph error type="descriptor">
              {t('Source settings: Confluence: Space key: Error')}
            </IbTypography.Paragraph>
          )}
        </Col>
      );
    };

    const renderPageUrls = () => {
      if (settings.loading_mode !== ConfluenceSourceParametersLoadingMode.Pages) {
        return null;
      }

      return (
        <Col span={24}>
          <IbTextArea
            placeholder={t('Source settings: Confluence: Pages list: Title')}
            status={errors.page_urls.isTouched && errors.page_urls.isError ? 'error' : 'default'}
            value={(settings.page_urls || []).join('\n')}
            onBlur={onPageUrlsBlur}
            onChange={onPageUrlsChange}
          />
          {errors.page_urls.isTouched && errors.page_urls.isError && (
            <IbTypography.Paragraph error type="descriptor">
              {t('Source settings: Confluence: Pages list: Error')}
            </IbTypography.Paragraph>
          )}
          <IbTypography.Paragraph type="descriptor">
            {t('Source settings: Confluence: Pages list: Descriptor')}
          </IbTypography.Paragraph>
        </Col>
      );
    };

    return (
      <div className={MAIN_CLASS_NAME}>
        <Row>
          <Col span={24}>
            <IbFormFieldLabel
              hint={t('Source settings: Common: Name: Hint')}
              label={t('Source settings: Confluence: Name: Title')}
            />
            <IbInput
              status={errors.name.isTouched && errors.name.isError ? 'error' : 'default'}
              value={parameters.name}
              onBlur={onNameBlur}
              onChange={onNameChange}
            />
            {errors.name.isTouched && errors.name.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: Confluence: Name: Error')}
              </IbTypography.Paragraph>
            )}
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <IbFormFieldLabel
              hint={t('Source settings: Confluence: Confluence URL: Hint')}
              label={t('Source settings: Confluence: Confluence URL: Title')}
            />
            <IbInput
              status={errors.base_url.isTouched && errors.base_url.isError ? 'error' : 'default'}
              value={settings.base_url}
              onBlur={onBaseUrlBlur}
              onChange={onBaseUrlChange}
            />
            {errors.base_url.isTouched && errors.base_url.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: Confluence: Confluence URL: Error')}
              </IbTypography.Paragraph>
            )}
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <IbFormFieldLabel
              hint={
                <Trans
                  components={[
                    <a
                      key={0}
                      href="https://developer.atlassian.com/cloud/confluence/basic-auth-for-rest-apis/"
                      rel="noreferrer"
                      target="_blank"
                    />,
                  ]}
                  i18nKey="Source settings: Confluence: API Key: Hint"
                  t={t}
                />
              }
              label={t('Source settings: Confluence: API Key: Title')}
            />
            <IbInput value={settings.auth_token} onChange={onApiKeyChange} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <IbFormFieldLabel
              hint={t('Source settings: Confluence: Loading mode: Hint')}
              label={t('Source settings: Confluence: Loading mode: Title')}
            />
            <Switcher options={loadingModeOptions} value={settings.loading_mode} onChange={onLoadingModeChange} />
          </Col>
          {renderSpaceKey()}
          {renderPageUrls()}
        </Row>
      </div>
    );
  }
);

export default SourceSettingsConfluence;
